/**
 * UX 1A-e — "has this worker seen the first-run page on this device".
 *
 * Stored in localStorage, per account. The plan asked for
 * `users/{uid}/settings.workerOnboardedAt`, but firestore.rules let the owner
 * write only `lastSignInAt` and `updatedAt` on their user doc
 * (`selfWritableUserKeys`), and the rules are not changed in this stage. The
 * consequence: the first-run page shows once per device, not once per account.
 * All of its steps are optional and repeatable from the profile, so a second
 * showing on a new phone is harmless.
 *
 * Every access is guarded: private browsing or a full quota must never block
 * the worker's way into the app — an unreadable flag counts as "seen".
 */

export const FIRST_RUN_PATH = '/worker/welcome';

const key = (uid: string) => `rm24.worker.firstRunAt.${uid}`;

/** True when the page was already shown, or when the flag cannot be read. */
export function hasSeenWorkerFirstRun(uid: string | null | undefined): boolean {
  if (!uid) return true;
  try {
    return window.localStorage.getItem(key(uid)) !== null;
  } catch {
    return true;
  }
}

export function markWorkerFirstRunSeen(uid: string | null | undefined, at = new Date()): void {
  if (!uid) return;
  try {
    if (window.localStorage.getItem(key(uid)) === null) {
      window.localStorage.setItem(key(uid), at.toISOString());
    }
  } catch {
    /* storage unavailable — the redirect guard treats that as seen */
  }
}
